import React, { createContext, useContext } from 'react';

import InvestigationDrawer from 'security-app/components/Investigations/Drawer';
import { singleton } from 'logic/singleton';

const InvestigationDrawerContext = createContext(null);

type Props = {
  children: React.ReactNode;
};

export function InvestigationDrawerProvider({ children }: Props) {
  const getCurrentInvestigationId = React.useCallback((): string | undefined => {
    try {
      return localStorage.getItem('currentInvestigationId') ?? undefined;
    } catch {
      return undefined;
    }
  }, []);

  const [showDrawer, setShowDrawer] = React.useState<boolean>(false);
  const [collapsed, setCollapsed] = React.useState<boolean>(true);
  const [fullScreen, setFullScreen] = React.useState<boolean>(false);
  const [selectedInvestigationId, setSelectedInvestigationId] = React.useState<string | undefined>(getCurrentInvestigationId());

  const updateInvestigationId = React.useCallback((investigationId: string) => {
    if (!investigationId) return;

    setSelectedInvestigationId(investigationId);

    try {
      localStorage.setItem('currentInvestigationId', investigationId);
      // eslint-disable-next-line no-empty
    } catch { }
  }, []);

  React.useEffect(() => {
    const id = getCurrentInvestigationId() ?? undefined;
    setShowDrawer(!!id);
    setCollapsed(!!id);
    setSelectedInvestigationId(id);
  }, [getCurrentInvestigationId]);

  const clearInvestigation = React.useCallback(() => {
    setSelectedInvestigationId(undefined);
    setShowDrawer(false);
    setCollapsed(true);
    setFullScreen(false);

    try {
      localStorage.removeItem('currentInvestigationId');
      // eslint-disable-next-line no-empty
    } catch {}
  }, []);

  const contextValue = React.useMemo(
    () => ({
      collapsed,
      setCollapsed,
      setShowDrawer,
      fullScreen,
      setFullScreen,
      clearInvestigation,
      selectedInvestigationId,
      setSelectedInvestigationId: updateInvestigationId,
    }),
    [collapsed, setCollapsed, setShowDrawer, fullScreen, setFullScreen, clearInvestigation, selectedInvestigationId, updateInvestigationId],
  );

  return (
    <InvestigationDrawerContext.Provider value={contextValue}>
      {showDrawer && <InvestigationDrawer />}
      {children}
    </InvestigationDrawerContext.Provider>
  );
}

export const useInvestigationDrawer = singleton(
  'security.useInvestigationDrawer',
  () => () => useContext(InvestigationDrawerContext),
);
